import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import FilterBar from "@/components/feature/FilterBar";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/animeData";
import type { AnimeFilter, SortKey } from "@/types/anime";
import { allAnime, filterAnime } from "@/utils/anime";

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filter: AnimeFilter = {
    category: searchParams.get("category") || "all",
    region: searchParams.get("region") || "all",
    year: searchParams.get("year") || "all",
    status: searchParams.get("status") || "all",
    sort: (searchParams.get("sort") as SortKey) || "updated",
  };

  const activeCategory = categories.find((item) => item.slug === filter.category);
  const results = useMemo(() => filterAnime(allAnime, filter), [searchParams]);
  const shown = results.slice(0, visible);

  usePageMeta({
    title: `${activeCategory ? activeCategory.name : "全部作品"} - 色情动漫作品分类`,
    description: activeCategory
      ? `色情动漫${activeCategory.name}分类收录${activeCategory.desc}相关成人向动画作品，可按地区、年份与更新状态筛选。`
      : "色情动漫作品分类页，收录爱情、奇幻、动作、校园、悬疑、剧情、治愈等题材的成人向动画作品，支持多条件筛选与排序。",
    keywords: `色情动漫,成人动漫,${activeCategory ? `${activeCategory.name}动漫,` : ""}动漫分类,动漫筛选,动漫作品`,
  });

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [searchParams]);

  const updateFilter = (patch: Partial<AnimeFilter>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "all" || (key === "sort" && value === "updated")) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next, { replace: true });
  };

  const resetFilter = () => setSearchParams({}, { replace: true });

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1 w-full">
        <section className="w-full border-b border-background-200/70 bg-background-100/60">
          <div className="w-full px-4 md:px-8 lg:px-12 pt-8 pb-10 md:pt-10 md:pb-12">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/category" className="hover:text-accent-400 transition-colors">动漫分类</Link>
              {activeCategory && (
                <>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-accent-400">{activeCategory.name}</span>
                </>
              )}
            </nav>

            <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] tracking-[0.3em] text-accent-500 mb-2">CATEGORY</p>
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 tracking-wide">
                  {activeCategory ? `${activeCategory.name}类作品` : "全部动漫作品"}
                </h1>
                <p className="mt-3 text-sm text-foreground-500 leading-relaxed max-w-2xl">
                  {activeCategory
                    ? `${activeCategory.desc}。以下作品均按你选择的地区、年份与更新状态实时筛选，排序方式可随时切换。`
                    : "这里收录了本站全部成人向动画作品资料。使用下方筛选面板，按题材、地区、年份与连载状态缩小范围。"}
                </p>
              </div>
              <div className="flex flex-col gap-1 text-xs text-foreground-500">
                <span>当前筛选结果</span>
                <span className="font-label text-2xl text-accent-400 tracking-widest leading-none">
                  {results.length} <span className="text-sm font-body text-foreground-500">部作品</span>
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-10">
          <FilterBar filter={filter} onChange={updateFilter} onReset={resetFilter} total={results.length} />

          <div className="mt-8">
            {results.length === 0 ? (
              <EmptyState
                title="没有符合条件的作品"
                description="当前的分类、地区、年份与状态组合下暂时没有作品。可以尝试放宽条件，或直接重置筛选查看全部内容。"
                onReset={resetFilter}
              />
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-7">
                  {shown.map((item) => (
                    <AnimeCard key={item.id} anime={item} />
                  ))}
                </div>
                <div className="mt-10 flex flex-col items-center gap-3">
                  <p className="text-xs text-foreground-500">
                    已显示 {shown.length} / {results.length} 部
                  </p>
                  {visible < results.length ? (
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                      className="h-11 px-8 rounded-md border border-accent-600/50 text-accent-400 hover:bg-accent-500/10 text-sm flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      加载更多
                      <i className="ri-arrow-down-line" />
                    </button>
                  ) : (
                    <span className="text-xs text-foreground-400">已经到底了</span>
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 pb-4">
          <div className="rounded-lg border border-background-300/50 bg-background-100/60 p-5 md:p-6">
            <h2 className="text-sm text-foreground-900 tracking-wide mb-4 flex items-center gap-2">
              <i className="ri-compass-3-line text-accent-500" />
              也可以直接进入其他分类
            </h2>
            <div className="flex flex-wrap gap-2">
              {categories.map((item) => (
                <Link
                  key={item.slug}
                  to={`/category?category=${encodeURIComponent(item.slug)}`}
                  className={`h-8 px-3.5 rounded-full text-xs border transition-colors flex items-center ${
                    filter.category === item.slug
                      ? "border-accent-600/60 text-accent-400 bg-accent-500/10"
                      : "border-background-300/70 text-foreground-600 hover:text-accent-400 hover:border-accent-600/60"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}